import { useState } from 'react';
import { Share2, Copy, Check, Twitter, Facebook, Linkedin, MessageCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export function SharePhotoModal({ open, onClose, photo, galleryName, shareToken }) {
  const [copied, setCopied] = useState(false);

  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/share/${shareToken}?photo=${photo?.id}`;
  };

  const shareUrl = getShareUrl();
  const shareText = `Check out this photo from ${galleryName}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: galleryName,
          text: shareText,
          url: shareUrl,
        });
        toast.success('Shared successfully!');
        onClose();
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const shareLinks = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'bg-black hover:bg-gray-800',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-[#0A66C2] hover:bg-[#004182]',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#128C7E]',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-slate-600 hover:bg-slate-700',
      url: `mailto:?subject=${encodeURIComponent(galleryName)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
    }
  ];

  const handleShareClick = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  const roundActions = [
    ...(typeof navigator !== 'undefined' && navigator.share ? [{ name: 'Share', icon: Share2, onClick: handleNativeShare }] : []),
    { name: 'Message', icon: MessageCircle, onClick: () => handleShareClick(shareLinks.find((l) => l.name === 'WhatsApp').url) },
    { name: 'Email', icon: Mail, onClick: () => handleShareClick(shareLinks.find((l) => l.name === 'Email').url) },
    { name: copied ? 'Copied' : 'Copy', icon: copied ? Check : Copy, onClick: handleCopyLink },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm font-tiq-body text-white border-0" style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader>
          <DialogTitle className="font-tiq-display uppercase flex items-center gap-2" style={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.015em' }}>
            <Share2 className="w-5 h-5 text-[#BF212F]" />
            Share photo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2" style={{ minWidth: 0 }}>
          {/* Photo preview (3:2) — absolutely-positioned img so it can't force the modal wider */}
          {photo && (
            <div className="relative overflow-hidden" style={{ width: '100%', minWidth: 0, borderRadius: 12, background: '#0f0f10', aspectRatio: '3 / 2' }}>
              <img
                src={photo.thumbnail_url || photo.preview_url}
                alt={photo.filename}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}

          {/* Round share actions */}
          <div className="flex items-center justify-center gap-5">
            {roundActions.map((a) => {
              const Icon = a.icon;
              return (
                <button key={a.name} onClick={a.onClick} className="flex flex-col items-center gap-2" style={{ cursor: 'pointer', background: 'transparent', border: 'none' }}>
                  <span className="flex items-center justify-center" style={{ width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)' }}>
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <span className="font-tiq-heading" style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{a.name}</span>
                </button>
              );
            })}
          </div>

          {/* Copy-link row */}
          <div className="flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 8px 8px 12px', minWidth: 0 }}>
            <span className="font-tiq-mono flex-1 truncate" style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', minWidth: 0 }}>{shareUrl}</span>
            <button onClick={handleCopyLink} className="font-tiq-heading inline-flex items-center gap-1.5 text-white flex-shrink-0" style={{ background: '#BF212F', borderRadius: 8, padding: '8px 12px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SharePhotoModal;
